import React from 'react'
import styles from './MicrositeTable.module.css'
import { Inbox } from 'lucide-react'

/**
 * MicrositeTable
 *
 * Reusable CRM-style data table. Columns are config-driven so each
 * list screen (Redirects, SEO Pages, Custom Pages, Blog, Popups,
 * Announcements, Tracking Scripts, Domains, Email Templates) can
 * reuse the same shell with different column definitions.
 *
 * Usage:
 *   <MicrositeTable
 *     columns={[
 *       { key: 'fromPath', label: 'From path' },
 *       { key: 'toPath', label: 'To path' },
 *       { key: 'redirectType', label: 'Type', render: (row) => <MicrositeRedirectTypeBadge value={row.redirectType} /> },
 *       { key: 'actions', label: '', align: 'right', render: (row) => (
 *           <button onClick={() => onEdit(row)}>Edit</button>
 *       )}
 *     ]}
 *     rows={redirects}
 *     getRowKey={(row) => row.ID}
 *     emptyMessage="No redirects yet"
 *   />
 */
const MicrositeTable = ({
    columns = [],
    rows = [],
    getRowKey = (row, idx) => row.ID ?? idx,
    emptyMessage = 'Nothing here yet',
    emptyDescription,
    loading = false,
    onRowClick
}) => {
    if (loading) {
        return (
            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            {columns.map((col) => (
                                <th
                                    key={col.key}
                                    className={styles.th}
                                    style={{ textAlign: col.align || 'left', width: col.width }}
                                >
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                </table>
                <div className={styles.skeletonWrap}>
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className={styles.skeletonRow} />
                    ))}
                </div>
            </div>
        )
    }

    if (rows.length === 0) {
        return (
            <div className={styles.emptyWrap}>
                <Inbox size={26} className={styles.emptyIcon} />
                <div className={styles.emptyTitle}>{emptyMessage}</div>
                {emptyDescription && <div className={styles.emptyDesc}>{emptyDescription}</div>}
            </div>
        )
    }

    return (
        <div className={styles.tableWrap}>
            <table className={styles.table}>
                <thead>
                    <tr>
                        {columns.map((col) => (
                            <th
                                key={col.key}
                                className={styles.th}
                                style={{ textAlign: col.align || 'left', width: col.width }}
                            >
                                {col.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, idx) => (
                        <tr
                            key={getRowKey(row, idx)}
                            className={`${styles.tr} ${onRowClick ? styles.trClickable : ''}`}
                            onClick={() => onRowClick?.(row)}
                        >
                            {columns.map((col) => (
                                <td
                                    key={col.key}
                                    className={styles.td}
                                    style={{ textAlign: col.align || 'left' }}
                                >
                                    {col.render ? col.render(row, idx) : row[col.key]}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

export default MicrositeTable